import { memo, useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import { useMe } from '../session/SessionProvider';
import { CLASS_VIEWS, type ClassView } from '../classrooms/ClassScreen';
import { Alert, Button } from '../../shared/ui/components';
import { DRAG_TYPE, THUMB_SIZES, useThumbSize, type BackgroundPick, type NewItem } from './ConsoleLibrary';
import { useContextMenu } from './ContextMenu';
import { WithPreview } from './WithPreview';
import { CameraFeed, listDevices, loadSources, mediaDevicesOk, newSourceId, saveSources, type VideoSource } from './sources';

/**
 * Fuentes: cámaras web y capturadoras USB de esta computadora. Empieza vacía; con «Nueva fuente» se
 * le pone nombre y se elige el dispositivo. Doble clic: de fondo en vivo detrás de la letra;
 * arrastrar a Programa: a pantalla completa. Clic derecho: editar o eliminar.
 */
export const SourcesTab = memo(function SourcesTab({ onAdd, onBackground }: { onAdd: (item: NewItem) => Promise<void>; onBackground: (pick: BackgroundPick) => Promise<void> }) {
  const thumbs = useThumbSize();
  const thumbStyle = thumbs.size === null ? undefined : ({ '--ewc-thumb': `${THUMB_SIZES[thumbs.size]}rem` } as CSSProperties);
  const [sources, setSources] = useState<VideoSource[]>(loadSources);
  const [editing, setEditing] = useState<VideoSource | 'nueva' | null>(null);
  const [addingClass, setAddingClass] = useState(false);
  const churchId = useMe().activeChurchId;
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const menu = useContextMenu();
  const [viewing, setViewing] = useState<string | null>(null);
  const item = (s: VideoSource): NewItem => ({ kind: 'media', title: s.name.slice(0, 120), camera: s.id });

  const store = (list: VideoSource[]) => {
    setSources(list);
    saveSources(list);
  };
  async function run(job: () => Promise<void>, message: string) {
    setError(null);
    setDone(null);
    try {
      await job();
      setDone(message);
    } catch (e) {
      setError(errorMessage(e));
    }
  }
  const asBackground = (s: VideoSource) => void run(() => onBackground({ resourceId: null, localFile: null, camera: s.id }), `«${s.name}» quedó de fondo detrás de la letra.`);
  const toProgram = (s: VideoSource) => void run(() => onAdd(item(s)), `«${s.name}» está en Programa.`);

  return (
    <WithPreview
      storageKey="keora.consola.vistaPrevia.fuentes"
      preview={
        viewing && sources.some((x) => x.id === viewing) ? (
          <div className="mediaprev">
            <div className="mediaprev__stage">
              <CameraFeed sourceId={viewing} />
            </div>
            <p className="mediaprev__name">{sources.find((x) => x.id === viewing)?.name}</p>
            <p className="small muted mediaprev__meta">{sources.find((x) => x.id === viewing)?.label}</p>
          </div>
        ) : (
          <p className="small muted withprev__empty">Toca una fuente para verla en vivo aquí.</p>
        )
      }
    >
    <div className="ewc__sources">
      {menu.element}
      {editing ? (
        <SourceDialog
          source={editing === 'nueva' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={(s) => {
            store(editing === 'nueva' ? [...sources, s] : sources.map((x) => (x.id === s.id ? s : x)));
            setEditing(null);
          }}
        />
      ) : null}
      {addingClass && churchId ? (
        <ClassSourceDialog
          churchId={churchId}
          onClose={() => setAddingClass(false)}
          onSave={(list) => {
            store([...sources, ...list]);
            setAddingClass(false);
          }}
        />
      ) : null}
      <div className="ewc__search">
        <Button type="button" variant="secondary" onClick={() => setEditing('nueva')}>
          Nueva fuente
        </Button>
        <Button type="button" variant="secondary" onClick={() => setAddingClass(true)}>
          Clase en vivo
        </Button>
        <span className="small muted">Cámaras web y capturadoras USB de esta computadora. Doble clic: de fondo detrás de la letra · Arrastrar a Programa: a pantalla completa.</span>
        <span className="ewc__zoom" role="group" aria-label="Tamaño de las miniaturas">
          <button type="button" className="ewc__zoom-btn" aria-label="Miniaturas más chicas" disabled={thumbs.size === 0} onClick={thumbs.smaller}>
            −
          </button>
          <button type="button" className="ewc__zoom-btn" aria-label="Miniaturas más grandes" disabled={thumbs.size === THUMB_SIZES.length - 1} onClick={thumbs.bigger}>
            +
          </button>
        </span>
      </div>
      {!mediaDevicesOk() ? <Alert tone="info">Este navegador no deja usar cámaras. Usa la app KEORA Multimedia, Chrome o Edge.</Alert> : null}
      {error ? <Alert>{error}</Alert> : null}
      {done ? (
        <p className="small ewc__done" role="status">
          {done}
        </p>
      ) : null}
      {sources.length === 0 ? <p className="small muted">Todavía no hay fuentes. Conecta la cámara o la capturadora y toca «Nueva fuente».</p> : null}
      <ul className="ewc__thumbs" aria-label="Fuentes de video" style={thumbStyle}>
        {sources.map((s) => (
          <li key={s.id} className="ewc__thumb-cell">
            <div
              className="ewc__thumb"
              title={`${s.name} · ${s.label}`}
              draggable
              onDragStart={(ev) => {
                ev.dataTransfer.setData(DRAG_TYPE, JSON.stringify(item(s)));
                ev.dataTransfer.setData('text/plain', s.name);
                ev.dataTransfer.effectAllowed = 'copy';
              }}
              onClick={() => setViewing(s.id)}
              onDoubleClick={() => asBackground(s)}
              onContextMenu={(ev) =>
                menu.open(ev, s.name, [
                  { label: 'Usar como fondo', onSelect: () => asBackground(s) },
                  { label: 'A Programa', onSelect: () => toProgram(s) },
                  ...(s.classLive ? [] : [{ label: 'Editar…', onSelect: () => setEditing(s) }]),
                  {
                    label: 'Eliminar',
                    danger: true,
                    onSelect: () => {
                      if (window.confirm(`¿Eliminar la fuente «${s.name}» de esta computadora? La cámara no se toca.`)) store(sources.filter((x) => x.id !== s.id));
                    },
                  },
                ])
              }
            >
              <span className="ewc__thumb-pic">
                <CameraFeed sourceId={s.id} />
                <span className="ewc__thumb-badge" title="En vivo">
                  ●
                </span>
              </span>
              <span className="ewc__thumb-name">{s.name}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
    </WithPreview>
  );
});

/** Nombre y dispositivo de una fuente (con micrófono opcional). */
function SourceDialog({ source, onSave, onClose }: { source: VideoSource | null; onSave: (s: VideoSource) => void; onClose: () => void }) {
  const [devices, setDevices] = useState<{ video: MediaDeviceInfo[]; audio: MediaDeviceInfo[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const load = () => {
    setLoading(true);
    setError(null);
    listDevices()
      .then(setDevices)
      .catch((e: unknown) => setError((e as { name?: string }).name === 'NotAllowedError' ? 'No se dio permiso para usar la cámara. Dale permiso y vuelve a intentarlo.' : errorMessage(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const name = String(data.get('name') ?? '').trim();
    const v = devices?.video.find((d) => d.deviceId === data.get('video'));
    const a = devices?.audio.find((d) => d.deviceId === data.get('audio'));
    if (!name) return setError('Ponle nombre a la fuente (por ejemplo «Cámara del púlpito»).');
    if (!v) return setError('Elige la cámara o capturadora.');
    onSave({ id: source?.id ?? newSourceId(), name: name.slice(0, 60), deviceId: v.deviceId, label: v.label, audioDeviceId: a?.deviceId ?? null, audioLabel: a?.label ?? null });
  }

  return (
    <div className="ctxdialog" role="presentation" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="ctxdialog__box" role="dialog" aria-modal="true" aria-label={source ? `Editar «${source.name}»` : 'Nueva fuente'} onSubmit={submit}>
        <h2 className="ctxdialog__title">{source ? `Editar «${source.name}»` : 'Nueva fuente'}</h2>
        {error ? <Alert>{error}</Alert> : null}
        <label className="ctxdialog__field">
          Nombre
          <input name="name" defaultValue={source?.name ?? ''} placeholder="Cámara del púlpito" maxLength={60} autoFocus required />
        </label>
        <label className="ctxdialog__field">
          Cámara o capturadora
          <select name="video" defaultValue={source?.deviceId ?? ''} disabled={!devices}>
            <option value="">{loading ? 'Buscando…' : 'Elige el dispositivo'}</option>
            {devices?.video.map((d, i) => (
              <option key={d.deviceId || i} value={d.deviceId}>
                {d.label || `Cámara ${i + 1}`}
              </option>
            ))}
          </select>
        </label>
        <label className="ctxdialog__field">
          Sonido (opcional)
          <select name="audio" defaultValue={source?.audioDeviceId ?? ''} disabled={!devices}>
            <option value="">Sin sonido</option>
            {devices?.audio.map((d, i) => (
              <option key={d.deviceId || i} value={d.deviceId}>
                {d.label || `Micrófono ${i + 1}`}
              </option>
            ))}
          </select>
        </label>
        {devices && devices.video.length === 0 ? <p className="small">No se ve ninguna cámara. Conéctala y toca «Buscar de nuevo».</p> : null}
        <div className="ctxdialog__actions">
          <Button type="button" variant="ghost" onClick={load} busy={loading}>
            Buscar de nuevo
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">Guardar</Button>
        </div>
      </form>
    </div>
  );
}

/** Fuente «Clase en vivo»: un salón de Raíces y las vistas que se quieren proyectar (una fuente por vista). */
function ClassSourceDialog({ churchId, onSave, onClose }: { churchId: string; onSave: (list: VideoSource[]) => void; onClose: () => void }) {
  const [rooms, setRooms] = useState<{ id: string; name: string; liveSessionId: string | null }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [room, setRoom] = useState('');
  const [views, setViews] = useState<ClassView[]>(Object.keys(CLASS_VIEWS) as ClassView[]);
  useEffect(() => {
    api
      .get<{ items: { id: string; name: string; liveSessionId: string | null }[] }>(`/organization/churches/${churchId}/classrooms-screen`)
      .then((r) => setRooms(r.items))
      .catch((e) => setError(errorMessage(e)));
  }, [churchId]);
  const chosen = rooms?.find((r) => r.id === room);
  return (
    <div className="ctxdialog" role="presentation" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="ctxdialog__box"
        role="dialog"
        aria-modal="true"
        aria-label="Clase en vivo"
        onSubmit={(e) => {
          e.preventDefault();
          if (!chosen) return setError('Elige el salón.');
          if (views.length === 0) return setError('Elige al menos una vista.');
          onSave(views.map((v) => ({ id: newSourceId(), name: `${chosen.name} · ${CLASS_VIEWS[v]}`.slice(0, 60), deviceId: '', label: 'Clase en vivo', classLive: { churchId, classroomId: chosen.id, view: v } })));
        }}
      >
        <h2 className="ctxdialog__title">Clase en vivo</h2>
        <p className="small muted">Proyecta la clase de Raíces que el maestro está dando en vivo. Cada vista queda como una fuente: arrástrala a Programa o úsala de fondo.</p>
        {error ? <Alert>{error}</Alert> : null}
        <label className="ctxdialog__field">
          Salón
          <select value={room} onChange={(e) => setRoom(e.target.value)} disabled={!rooms}>
            <option value="">{rooms ? 'Elige el salón' : 'Cargando…'}</option>
            {rooms?.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.liveSessionId ? ' · 🔴 en vivo' : ''}
              </option>
            ))}
          </select>
        </label>
        <fieldset className="ctxdialog__field">
          <legend>Vistas</legend>
          {(Object.keys(CLASS_VIEWS) as ClassView[]).map((v) => (
            <label key={v} className="check">
              <input type="checkbox" checked={views.includes(v)} onChange={(e) => setViews((xs) => (e.target.checked ? [...xs, v] : xs.filter((x) => x !== v)))} /> {CLASS_VIEWS[v]}
            </label>
          ))}
        </fieldset>
        <div className="ctxdialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">Agregar</Button>
        </div>
      </form>
    </div>
  );
}
